/**
 * Dashboard Field System — class names for inputs inside `.dashboard-panel`.
 * Tokens → src/styles/dashboard-field-tokens.css
 *
 * Component presets: default, search, page, upload, display, inline-{96|120|140|160|240},
 * payment-default, payment-inline-{preset}, verification-digit.
 * Legacy size strings (medium, short, micro, …) map to presets for backward compatibility.
 */

export const DASHBOARD_FIELD_BASE =
  "dashboard-field dashboard-field-input box-border min-w-0 font-inter text-offblack border border-solid border-mediumgray bg-white tp-field-focus focus:outline-none placeholder-lightgray appearance-none";

/** @deprecated Prefer FIELD_PRESET keys — kept for Tailwind content scan */
export const DASHBOARD_FIELD_SIZE_CLASS = {
  micro: "dashboard-field--inline-96",
  short: "dashboard-field--inline-160",
  small: "dashboard-field--inline-240",
  medium: "dashboard-field--input-default",
  large: "dashboard-field--large",
  search: "dashboard-field--search",
  upload: "dashboard-field--upload",
  page: "dashboard-field--page",
  default: "dashboard-field--input-default",
  display: "dashboard-field--display",
  "inline-96": "dashboard-field--inline-96",
  "inline-120": "dashboard-field--inline-120",
  "inline-140": "dashboard-field--inline-140",
  "inline-160": "dashboard-field--inline-160",
  "inline-240": "dashboard-field--inline-240",
  "payment-default": "dashboard-field--payment-default",
  "payment-inline-96": "dashboard-field--payment-inline-96",
  "payment-inline-120": "dashboard-field--payment-inline-120",
  "payment-inline-140": "dashboard-field--payment-inline-140",
  "payment-inline-160": "dashboard-field--payment-inline-160",
  "payment-inline-240": "dashboard-field--payment-inline-240",
};

const LEGACY_SIZE_MAP = {
  micro: "inline-96",
  short: "inline-160",
  small: "inline-240",
  medium: "default",
  large: "large",
  lg: "large",
  md: "default",
  sm: "inline-140",
};

const INLINE_PRESETS = new Set(["96", "120", "140", "160", "240"]);

function resolveFieldPreset(sizeOrOptions) {
  if (sizeOrOptions == null || sizeOrOptions === "") {
    return "default";
  }
  if (typeof sizeOrOptions === "object") {
    const { component, variant, preset, pattern } = sizeOrOptions;
    if (component === "verification-digit") return "verification-digit";
    if (component === "search") return "search";
    if (component === "page") return "page";
    if (component === "upload") return "upload";
    if (component === "display") return "display";
    if (component === "payment") {
      const p = preset || pattern || "default";
      return p === "default" ? "payment-default" : `payment-inline-${p}`;
    }
    if (component === "inline" || variant === "inline") {
      const p = String(preset || pattern || "140");
      return INLINE_PRESETS.has(p) ? `inline-${p}` : "inline-140";
    }
    return "default";
  }

  const key = String(sizeOrOptions);
  if (LEGACY_SIZE_MAP[key]) {
    return LEGACY_SIZE_MAP[key];
  }
  if (DASHBOARD_FIELD_SIZE_CLASS[key]) {
    return key;
  }
  if (INLINE_PRESETS.has(key)) {
    return `inline-${key}`;
  }
  return "default";
}

function presetToModifier(preset) {
  if (preset === "verification-digit") {
    return "dashboard-field--verification-digit";
  }
  return DASHBOARD_FIELD_SIZE_CLASS[preset] || DASHBOARD_FIELD_SIZE_CLASS.default;
}

/**
 * Field shell classes for a component preset or legacy size alias.
 * @param {string|object} [sizeOrOptions='default']
 */
export const dashboardFieldClassNames = (sizeOrOptions = "default") => {
  const preset = resolveFieldPreset(sizeOrOptions);
  const modifier = presetToModifier(preset);
  const layout =
    preset === "verification-digit"
      ? "inline-block w-auto"
      : preset.startsWith("inline-") || preset.startsWith("payment-inline-")
        ? "inline-block w-auto"
        : "block w-full";
  return `${DASHBOARD_FIELD_BASE} ${modifier} ${layout}`.trim();
};

/** Input Field — Inline width preset (96, 120, 140, 160, 240). */
export const dashboardInlineFieldClassNames = (preset = "140", extra = "") =>
  `${dashboardFieldClassNames({ component: "inline", preset })} ${extra}`.trim();

/** Payment Input — default or inline preset. */
export const dashboardPaymentFieldClassNames = (
  preset = "default",
  extra = ""
) => {
  const resolved =
    preset === "default" ? "payment-default" : `payment-inline-${preset}`;
  return `${dashboardFieldClassNames(resolved)} ${extra}`.trim();
};

/** Verification Code Input — single digit box. */
export const dashboardVerificationDigitClassNames = (extra = "") =>
  `${DASHBOARD_FIELD_BASE} dashboard-field--verification-digit ${extra}`.trim();

/** Verification Code Input — digit group wrapper (4 or 6). */
export const dashboardVerificationGroupClassNames = (digits = 6) => {
  const count = digits === 4 ? 4 : 6;
  return `dashboard-field-verification-group dashboard-field-verification-group--${count}`;
};

export const DASHBOARD_FIELD_LABEL_CLASS = "dashboard-field-label text-offblack";
export const DASHBOARD_FIELD_ERROR_CLASS = "dashboard-field-error text-alertred";
